import { useState, useEffect } from 'react';

/**
 * Custom hook for managing PWA updates
 * Detects when a new version is available and provides update functionality
 */
const usePWAUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    // Check if service worker is supported
    if ('serviceWorker' in navigator) {
      // Get the current registration
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          setRegistration(reg);
          
          // Check if there's already a waiting worker
          if (reg.waiting) {
            setWaitingWorker(reg.waiting);
            setUpdateAvailable(true);
          }
        }
      });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          setUpdateAvailable(true);
        }
      });

      // Listen for new service worker waiting to activate
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Service worker has been updated and activated
        window.location.reload();
      });

      // Check for updates periodically (every 30 minutes)
      const checkInterval = setInterval(() => {
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg) {
            reg.update();
          }
        });
      }, 30 * 60 * 1000); // 30 minutes

      return () => clearInterval(checkInterval);
    }
  }, []);

  // Function to apply the update
  const applyUpdate = () => {
    if (waitingWorker) {
      // Tell the waiting service worker to activate
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Just reload the page
      window.location.reload();
    }
  };

  // Function to manually check for updates
  const checkForUpdate = async () => {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.update();
        if (reg.waiting) {
          setWaitingWorker(reg.waiting);
          setUpdateAvailable(true);
          return true;
        }
      }
    }
    return false;
  };

  return {
    updateAvailable,
    applyUpdate,
    checkForUpdate
  };
};

export default usePWAUpdate;
